import moment from 'moment';
import { TimeUnit } from 'types/common';

export enum dateFormats {
  date = 'YYYY-MM-DD',
  longDate = 'YYYY-MM-DDTHH:mm:ss.SSSS',
  longDateAlternative = 'YYYY-MM-DD HH:mm:ss.SSS',
  readableDate = 'DD.MM.YYYY',
  readableDateTime = 'DD.MM.YYYY HH:mm',
  readableDateTimeSeconds = 'DD.MM.YYYY HH:mm:ss',
}

const timeUnitDesc: Record<TimeUnit, { one: string | string[], short: string | string[]; many: string | string[] }> = {
  [TimeUnit.SECOND]: {
    one: 'секунда',
    short: 'сек',
    many: 'секунд',
  },
  [TimeUnit.MINUTE]: {
    one: 'минута',
    short: 'мин',
    many: 'минут',
  },
  [TimeUnit.HOUR]: {
    one: 'час',
    short: 'ч',
    many: 'часов',
  },
  [TimeUnit.DAY]: {
    one: ['день', 'дня'],
    short: 'д',
    many: 'дней',
  },
}

export const getDayStart = (
  format: string = dateFormats.longDate,
  utc: boolean = true
) =>
  utc
    ? moment(new Date())
        .startOf('d')
        .utc()
        .format(format)
    : moment(new Date())
        .startOf('d')
        .format(format);
export const getCurrentDate = (
  format: string = dateFormats.longDate,
  utc: boolean = true
) =>
  utc
    ? moment(new Date())
        .utc()
        .format(format)
    : moment(new Date()).format(format);

export const getDayStartAsDate = () =>
  moment(new Date())
    .startOf('d')
    .utc()
    .toDate();
export const getCurrentTimeAsDate = () =>
  moment(new Date())
    .utc()
    .toDate();

export const getParamsDayStart = () =>
  getDayStart(dateFormats.longDateAlternative);
export const getParamsCurrentDate = () =>
  getCurrentDate(dateFormats.longDateAlternative);

export const getReadableDayStart = () =>
  getDayStart(dateFormats.readableDateTime);
export const getReadableCurrentDate = () =>
  getCurrentDate(dateFormats.readableDateTime);

export const toNormalDate = (date: string | undefined) =>
  moment(date).format('DD.MM.YYYY HH:MM');

export const formatTime = (dTime: string) => {
  if (dTime.length === 0) return dTime;
  const parts = dTime.split(':');
  if (parts.length < 2) {
    parts.push('0');
  }
  if (parseInt(parts[0]) > 23) {
    parts[0] = '00';
  }
  if (parseInt(parts[1]) > 59) {
    parts[1] = '0';
  }
  return `${String(parseInt(parts[0])).padStart(2, '0')}:${String(
    parseInt(parts[1])
  ).padStart(2, '0')}`;
};

export const formatHours = (hours: number) =>
  hours < 10 ? '0' + hours : hours;

export const numberToHour = (
  value: number | string,
  format: string,
  local?: boolean
) =>
  local
    ? moment(value, 'HH')
        .local()
        .format(format)
    : moment
        .utc(value, 'HH')
        .local()
        .format(format);

export const hourToPeriod = (
  hour: string | number,
  skipZeros?: boolean,
  local?: boolean
) => {
  const format = skipZeros ? 'HH' : 'HH:00';
  const endDate = local
    ? moment(hour, 'HH')
        .add(1, 'h')
        .format(format)
    : moment
        .utc(hour, 'HH')
        .local()
        .add(1, 'h')
        .format(format);
  return `${numberToHour(hour, format, local)}-${endDate}`;
};

export const months: Record<string, string> = {
  1: 'Январь',
  2: 'Февраль',
  3: 'Март',
  4: 'Апрель',
  5: 'Май',
  6: 'Июнь',
  7: 'Июль',
  8: 'Август',
  9: 'Сентябрь',
  10: 'Октября',
  11: 'Ноябрь',
  12: 'Декабрь',
};

export const monthsShort: Record<string, string> = {
  1: 'янв',
  2: 'фев',
  3: 'марта',
  4: 'апр',
  5: 'мая',
  6: 'июня',
  7: 'июля',
  8: 'авг',
  9: 'сент',
  10: 'окт',
  11: 'ноя',
  12: 'дек',
};

export const getDatesIntervalArray = (
  startDate: moment.Moment,
  endDate: moment.Moment,
  format: string = dateFormats.date
): string[] => {
  const result = [];
  for (
    let m = moment(startDate);
    m.isSameOrBefore(moment(endDate));
    m.add(1, 'd')
  ) {
    result.push(m.format(format));
  }
  return result;
};

export const shortDows = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

export const formatMinutes = (minutes: number) =>
  moment()
    .startOf('day')
    .add(minutes, 'minutes')
    .format('HH:mm');

export const sortDate = (
  dateA: Date | string,
  dateB: Date | string,
): number => {
  const momentDateA = moment(dateA, dateFormats.readableDateTimeSeconds);
  const momentDateB = moment(dateB, dateFormats.readableDateTimeSeconds);

  if (momentDateA.isAfter(momentDateB)) {
    return -1;
  }
  if (momentDateA.isBefore(momentDateB)) {
    return 1;
  }

  return 0;
}

export const getDeclinationOfDates = (value: number, unit: TimeUnit, isShort: boolean = true) => {
  const getDesc = (unit: TimeUnit, variant: keyof typeof timeUnitDesc[0]): string => {
    const variantProp = timeUnitDesc[unit][variant];
    return Array.isArray(variantProp)
           ? variantProp[0]
           : variantProp;
  }

  if (isShort) {
    return getDesc(unit, 'short');
  }

  if (unit === TimeUnit.DAY && [2, 3, 4].includes(value % 10)) {
    return timeUnitDesc[unit].many[1];
  }

  if (value % 10 === 1 && value !== 11) {
    return getDesc(unit, 'one');
  }
  return getDesc(unit, 'many');
};
